<script>
  import {
    drawAssetLayer,
    drawTemplateLayer,
    drawTerrainLayer,
    handleSelectFirstFrame,
  } from './utils/canvas'
  import { onMount } from 'svelte'
  import { state, jsonData, defaultValueMetadata } from './stores'
  import { printPrettyJson } from './utils/jsonHighlight'

  export let blockTemplates
  export let config

  let defaultFileName = ''
  let id = ''
  let type = ''
  let url = ''
  let originalImageSize = [0, 0]
  let dimensions = [1, 1, 1]
  let stackable = false
  let overrideX = 0
  let overrideY = 0

  let templateIndex

  let currentJson = {}
  let currentState = {}

  onMount(() => {
    drawTerrainLayer()

    state.subscribe((stt) => {
      currentState = stt
    })

    jsonData.subscribe((val) => {
      printPrettyJson(val)
      localStorage.setItem('t_asset_metadata_', val)
      currentJson = JSON.parse(val)
    })
  })

  function handleUpdateJson(key, value) {
    currentJson[key] = value
    jsonData.update((val) => {
      const data = JSON.parse(val)
      const updates = { ...data, ...currentJson }
      return JSON.stringify(updates)
    })
  }

  function handleUpdateAssetLocation(overrides) {
    const imageAsset = currentState.asset.img
    const [x, y] = getTargetPosition()
    const offX = getOffsetX(dimensions[0], dimensions[1])
    drawAssetLayer(imageAsset, x + offX, y, overrides)
  }

  function predictBlockSizeDimensions(width) {
    const X = Math.floor(width / 36)

    dimensions = [X, X, X]
    handleUpdateJson('dimensions', dimensions)
    return dimensions
  }

  function getOffsetX(x, y) {
    const offset = 9
    return (y - x) * offset
  }

  function handleLoadImageAsset(event) {
    const _URL = window.URL || window.webkitURL
    const file = event?.target.files && event.target.files[0]
    // FileReader support
    if (FileReader && file) {
      const img = new Image()
      const objectUrl = _URL.createObjectURL(file)
      img.src = objectUrl
      img.onload = function () {
        const actualHeight = this.height / config.TOTAL_FRAME_SPRITESHEET
        const isSpritesheet = actualHeight >= this.width
        const imageProp = {
          width: this.width,
          height: isSpritesheet ? actualHeight : this.height,
          name: file.name,
          img: isSpritesheet
            ? handleSelectFirstFrame(this.width, actualHeight, img)
            : img,
        }
        state.update((val) => ({ ...val, asset: imageProp, template: null }))
        _URL.revokeObjectURL(objectUrl)
        handleUpdateImageProperty(imageProp)
        // reset overrides
        overrideX = 0
        handleUpdateJson('overrideX', overrideX)
        overrideY = 0
        handleUpdateJson('overrideY', overrideY)
        // draw asset to canvas
        // we try predict block size by image width
        const predictDimensions = predictBlockSizeDimensions(this.width)
        const offX = getOffsetX(predictDimensions[0], predictDimensions[1])
        const [x, y] = getTargetPosition()
        drawAssetLayer(imageProp.img, x + offX, y)
        // draw template
        drawTemplateLayer(predictDimensions, currentState.tiles)
      }
    }
    // Not supported
    else {
      // fallback -- perhaps submit the input to an iframe and temporarily store
      // them on the server until the user's session ends.
      alert('Your browser is not supported to load image')
    }
  }

  function handleUpdateImageProperty(image) {
    const names = image.name.split('_')
    const lastIndex = 3
    const updateData = {
      defaultFileName: image.name,
      id: image.name
        .replace('.png', '')
        .replace(/\s/gi, '')
        .replace('_spritesheet', '')
        .replace('_x1', ''),
      originalImageSize: [image.width, image.height],
      type: names[1] || '',
      url: names.filter((_, idx) => idx > 0 && idx < lastIndex).join('/'),
    }
    const mergedData = { ...currentJson, ...updateData }
    id = mergedData.id
    type = mergedData.type
    url = mergedData.url
    defaultFileName = mergedData.defaultFileName
    originalImageSize[0] = mergedData.originalImageSize[0]
    originalImageSize[1] = mergedData.originalImageSize[1]

    jsonData.update(() => {
      return JSON.stringify(mergedData)
    })
  }

  const handleUpdateFormValue = ({ target }, key) => {
    switch (key) {
      case 'stackable':
        stackable = target.checked
        handleUpdateJson(key, stackable)
        break
      case 'url':
        url = target.value
        handleUpdateJson(key, url)
        break
      case 'type':
        type = target.value
        handleUpdateJson(key, type)
        break
      case 'overrideX':
        overrideX = Number(target.value)
        handleUpdateJson(key, overrideX)
        handleUpdateAssetLocation([overrideX, overrideY])
        break
      case 'overrideY':
        overrideY = Number(target.value)
        handleUpdateJson(key, overrideY)
        handleUpdateAssetLocation([overrideX, overrideY])
        break
      default:
        if (key.includes('dimensions')) {
          const [keyString, indexString] = key.split(':')
          dimensions[Number(indexString)] = Number(target.value)
          handleUpdateJson(keyString, dimensions)
          drawTemplateLayer(dimensions, currentState.tiles)
          // draw asset with new Position
          if (currentState.asset) {
            const overrides = [overrideX, overrideY]
            const offX = getOffsetX(dimensions[0], dimensions[1])
            const [x, y] = getTargetPosition()
            drawAssetLayer(currentState.asset.img, x + offX, y, overrides)
          }
        }
    }
  }

  function clearImageAsset() {
    const CANVAS_ID = 'canvas-asset'
    let canvas = document.getElementById(CANVAS_ID)
    const ctx = canvas.getContext('2d')
    ctx.clearRect(0, 0, canvas.width, canvas.height)
  }

  function handleClearData() {
    clearImageAsset()

    defaultFileName = ''
    id = ''
    type = ''
    url = ''
    originalImageSize = [0, 0]
    dimensions = [1, 1, 1]
    stackable = false

    jsonData.set(JSON.stringify(defaultValueMetadata))
    state.update((val) => ({ ...val, asset: undefined, template: undefined }))
  }

  function getTargetPosition(idx = 55) {
    const tile = currentState.tiles[idx]
    const posX = tile.position.x
    const posY = tile.position.y + config.OFFSET_HEIGHT
    return [posX, posY]
  }

  function exportToJsonFile(jsonData) {
    let dataStr = JSON.stringify(jsonData, null, 2)
    let dataUri =
      'data:application/json;charset=utf-8,' + encodeURIComponent(dataStr)

    let exportFileDefaultName = `${jsonData.id || 'asset_data'}.json`

    let linkElement = document.createElement('a')
    linkElement.setAttribute('href', dataUri)
    linkElement.setAttribute('download', exportFileDefaultName)
    linkElement.click()
  }
</script>

<main>
  <div id="app">
    <h3 style="background-color: black; color: #fff; padding: 6px;">
      T! asset metadata
    </h3>
    <div class="row">
      <div class="six columns">
        <label for="select-terrain">Select Terrain</label>
        <select
          on:change={({ target }) => drawTerrainLayer(target.value)}
          on:blur={() => console.info('#blur')}
          class="u-full-width"
          id="select-terrain"
        >
          <option value="road_plain">Road Plain</option>
          <option value="grass_1">Grass</option>
          <option value="water_pond">Water Pond</option>
        </select>
        <div>
          <label for="file-upload" class="custom-file-upload">
            Upload Image Asset
          </label>
          <input
            on:change={handleLoadImageAsset}
            accept="image/png"
            id="file-upload"
            type="file"
          />
        </div>
        <div>
          <label for="defaultFileName">File Name</label>
          <input
            disabled
            class="u-full-width"
            type="text"
            id="defaultFileName"
            bind:value={defaultFileName}
          />
        </div>
        <div id="canvasRoot">
          <canvas id="canvasiso" />
          <canvas id="canvas-asset" />
          <canvas id="canvas-template" />
        </div>
        <!-- <Canvas /> -->
      </div>

      <div class={`six columns ${currentState.asset ? '' : 'display-none'}`}>
        <form
          id="meta-form"
          on:submit={(e) => {
            e.preventDefault()
            exportToJsonFile(currentJson)
          }}
        >
          <label for="dimensions">Template Dimensions</label>
          <div class="row">
            <div class="four columns">
              <label for="dimX">X</label>
              <input
                class="u-full-width"
                type="number"
                placeholder="0"
                min={1}
                id="dimX"
                bind:value={dimensions[0]}
                on:input={(e) => handleUpdateFormValue(e, 'dimensions:0')}
              />
            </div>
            <div class="four columns">
              <label for="dimY">Y</label>
              <input
                class="u-full-width"
                type="number"
                placeholder="0"
                min={1}
                id="dimY"
                bind:value={dimensions[1]}
                on:input={(e) => handleUpdateFormValue(e, 'dimensions:1')}
              />
            </div>
            <div class="four columns">
              <label for="dimZ">Z</label>
              <input
                class="u-full-width"
                type="number"
                placeholder="0"
                min={1}
                id="dimZ"
                bind:value={dimensions[2]}
                on:input={(e) => handleUpdateFormValue(e, 'dimensions:2')}
              />
            </div>
          </div>

          <label for="type">Type</label>
          <input
            class="u-full-width"
            type="text"
            placeholder="terrain"
            id="type"
            bind:value={type}
            on:input={(e) => handleUpdateFormValue(e, 'type')}
            required
          />

          <label for="url">Folder Path</label>
          <small
            >The path is folder location after "Tatsu Works/1A - Dev Assets/Base
            Assets/env" on dropbox. i.e: prop/water</small
          >
          <input
            class="u-full-width"
            type="text"
            placeholder="prop/water"
            id="url"
            bind:value={url}
            on:input={(e) => handleUpdateFormValue(e, 'url')}
            required
          />

          <div class="row">
            <div class="six columns">
              <label for="overX">Override X Position</label>
              <input
                class="u-full-width"
                type="number"
                placeholder="0"
                id="overX"
                bind:value={overrideX}
                on:input={(e) => handleUpdateFormValue(e, 'overrideX')}
              />
            </div>
            <div class="six columns">
              <label for="overY">Override Y Position</label>
              <input
                class="u-full-width"
                type="number"
                placeholder="0"
                id="overY"
                bind:value={overrideY}
                on:input={(e) => handleUpdateFormValue(e, 'overrideY')}
              />
            </div>
          </div>

          <label>
            <input
              on:input={(e) => handleUpdateFormValue(e, 'stackable')}
              type="checkbox"
              id="stackable"
              bind:value={stackable}
            />
            <label for="stackable" class="label-body">Stackable</label>
          </label>

          <!-- <input class="button-primary" type="submit" value="Submit"> -->
        </form>
      </div>
    </div>

    <div class="row">
      <div class="six columns">
        <h4>JSON Preview</h4>
      </div>
      <div class="six columns">
        <button
          class="button-primary u-pull-right"
          form="meta-form"
          type="submit"
        >
          Export JSON
        </button>
        <button
          class="u-pull-right"
          style="margin-right: 6px;"
          on:click={handleClearData}>Clear Data</button
        >
      </div>
    </div>

    <div id="jsonPreview" />
  </div>
</main>

<style>
  #app {
    max-width: 900px;
    width: 100%;
    margin: 0 auto;
  }
  #canvasRoot {
    display: block;
    position: relative;
    width: 100%;
    min-height: 380px;
  }

  #canvasiso {
    display: block;
    margin-bottom: 20px;
    position: absolute;
  }

  #canvas-asset,
  #canvas-template {
    display: block;
    position: absolute;
  }

  #file-upload {
    display: none;
  }

  .custom-file-upload {
    border: 1px solid #ccc;
    display: inline-block;
    padding: 6px 12px;
    cursor: pointer;
  }

  .list-template {
    display: block;
  }

  .img-template {
    padding: 16px;
    cursor: pointer;
    border: 1px solid transparent;
  }

  .img-template:hover {
    background: #eee;
  }

  .img-template-selected {
    border: 1px solid #eee;
  }

  .hide-form {
    visibility: hidden;
  }
  .display-none {
    display: none;
  }
</style>
